import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getItem, setItem, COOKIE_CONSENT_KEY } from '@/shared/lib/localStorage';

export function CookieBanner() {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(() => !getItem(COOKIE_CONSENT_KEY));

  const handleAccept = () => {
    setItem(COOKIE_CONSENT_KEY, 'accepted');
    setIsVisible(false);
  };

  const handleReject = () => {
    setItem(COOKIE_CONSENT_KEY, 'rejected');
    setIsVisible(false);
  };

  const handleDismiss = () => {
    setItem(COOKIE_CONSENT_KEY, 'dismissed');
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Overlay backdrop - only visible when modal is shown */}
      <button
        type="button"
        className="fixed inset-0 z-[89] bg-black/20 md:hidden"
        onClick={handleDismiss}
        aria-label="Close cookie banner"
      />

      {/* Desktop version - bottom left */}
      <div className="fixed bottom-6 left-6 z-[90] hidden w-80 rounded-3xl border border-gray-200 bg-white p-5 shadow-2xl md:block">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="mt-2 nav-label text-gray-900">{t('common.cookies.title', 'We use cookies')}</h3>
            <p className="mt-2 text-xs leading-5 text-gray-600">
              {t(
                'common.cookies.description',
                'Some cookies are essential for functionality. Others help us improve your experience.'
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={handleDismiss}
            className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded text-gray-400 hover:text-gray-600"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-4 space-y-2">
          <Link to="/legal/cookies" className="flex items-center gap-2 py-2 desc-md !text-gray-800">
            {t('common.cookies.readPolicy', 'Read the policy')} <ChevronRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="mt-4 flex gap-2">
          <button
            type="button"
            onClick={handleReject}
            className="flex-1 rounded-full border border-gray-300 px-3 py-2.5 text-xs font-bold text-gray-700 transition-colors hover:bg-gray-50"
          >
            {t('common.cookies.reject', 'Reject')}
          </button>
          <button
            type="button"
            onClick={handleAccept}
            className="flex-1 rounded-full bg-lime-900 px-3 py-2.5 text-xs font-semibold text-lime-200 transition-colors hover:bg-lime-800"
          >
            {t('common.cookies.accept', 'Accept')}
          </button>
        </div>
      </div>

      {/* Mobile version - center bottom */}
      <div className="fixed inset-x-3 bottom-3 z-[90] rounded-3xl border border-gray-200 bg-white p-4 shadow-2xl md:hidden sm:inset-x-4 sm:bottom-4">
        <div className="flex items-start justify-between gap-2 sm:gap-3">
          <div className="flex-1">
            <p className="eyebrow text-lime-400">{t('common.cookies.preferences', 'Preferences')}</p>
            <h3 className="mt-1.5 nav-label text-gray-900">{t('common.cookies.title', 'We use cookies')}</h3>
            <p className="mt-1.5 text-xs leading-4 text-gray-600">
              {t(
                'common.cookies.description',
                'Some cookies are essential for functionality. Others help us improve your experience.'
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={handleDismiss}
            className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded text-gray-400 hover:text-gray-600"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-3 flex gap-2">
          <button
            type="button"
            onClick={handleReject}
            className="flex-1 rounded-full border border-gray-300 px-3 py-2 text-xs font-bold text-gray-700 transition-colors hover:bg-gray-50"
          >
            {t('common.cookies.reject', 'Reject')}
          </button>
          <button
            type="button"
            onClick={handleAccept}
            className="flex-1 rounded-full bg-lime-900 px-3 py-2 text-xs font-bold text-white transition-colors hover:bg-lime-800"
          >
            {t('common.cookies.accept', 'Accept')}
          </button>
        </div>

        <Link
          to="/legal/cookies"
          className="mt-2 block text-center desc-sm !font-semibold !text-lime-700 hover:!text-lime-800"
        >
          {t('common.cookies.readPolicy', 'Read the policy')}
        </Link>
      </div>
    </>
  );
}
